import React from 'react';
import { Card, Row, Col, Statistic, Progress, Typography, Table, Tag, Spin, Alert, Space } from 'antd';
import {
    RobotOutlined,
    DollarOutlined,
    FireOutlined,
    RiseOutlined,
    ThunderboltOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import { useGetAdminTokenUsageQuery, useGetAIExpertsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import type { ITokenUsageSummary } from '../../services/nestjs-base-api';

const { Title, Text } = Typography;

export const AITokenUsageWidget: React.FC = () => {
    const { user } = useAuth();
    
    // Fetch token usage data for the platform (no orgId = platform-wide)
    const { data: tokenData, isLoading, error } = useGetAdminTokenUsageQuery({
        jwtToken: user?.jwtToken || '',
        days: 30, // Last 30 days
    }, {
        skip: !user?.jwtToken
    });

    // Fetch AI experts data for dynamic names
    const { data: aiExpertsData } = useGetAIExpertsQuery({
        jwtToken: user?.jwtToken || '',
    }, {
        skip: !user?.jwtToken
    });

    if (isLoading) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading AI usage data...</div>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <Alert
                    message="Error loading AI token usage data"
                    description="Unable to fetch token usage statistics. Please try again later."
                    type="warning"
                    showIcon
                    style={{ margin: '20px 0' }}
                />
            </Card>
        );
    }

    if (!tokenData) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">No AI token usage data available</Text>
                </div>
            </Card>
        );
    }

    return <AITokenUsageDisplay data={tokenData} aiExpertsData={aiExpertsData} />;
};

const AITokenUsageDisplay: React.FC<{ data: ITokenUsageSummary; aiExpertsData: any }> = ({ data, aiExpertsData }) => {
    const formatTokenCount = (tokens: number): string => {
        if (tokens >= 1000000) {
            return `${(tokens / 1000000).toFixed(2)}M`;
        } else if (tokens >= 1000) {
            return `${(tokens / 1000).toFixed(1)}k`;
        }
        return tokens.toString();
    };

    const formatFeatureType = (type: string): string => {
        // Build dynamic types from AI experts data
        const types: Record<string, string> = {
            'element_analysis_measured': 'Measured Elements',
            'element_analysis_latent': 'Latent Elements',
            'ai_experts': 'AI Experts Chat',
            'dashboard_ai_expert_dashboard_overview': 'Dashboard AI - Overview',
            'response-monitoring': 'Response Monitoring',
        };

        // Add dynamic expert names from API
        if (aiExpertsData) {
            aiExpertsData.forEach((expert: any) => {
                types[`ai_expert_${expert.expertType}`] = expert.name;
                types[`dashboard_ai_expert_${expert.expertType}`] = `Dashboard AI - ${expert.name}`;
            });
        }

        return types[type] || type;
    };

    const getFeatureColor = (type: string): string => {
        const colors: Record<string, string> = {
            'element_analysis_measured': 'blue',
            'element_analysis_latent': 'purple',
            'ai_experts': 'green',
            'dashboard_ai_expert_dashboard_overview': 'orange',
            'response-monitoring': 'red',
        };

        // Add dynamic colors for experts from API
        if (aiExpertsData) {
            const colorOptions = ['cyan', 'geekblue', 'volcano', 'magenta', 'lime', 'gold'];
            aiExpertsData.forEach((expert: any, index: number) => {
                const color = colorOptions[index % colorOptions.length];
                colors[`ai_expert_${expert.expertType}`] = color;
                colors[`dashboard_ai_expert_${expert.expertType}`] = color;
            });
        }

        return colors[type] || 'default';
    };

    const topOrgColumns = [
        {
            title: 'Organization',
            dataIndex: 'organisationName',
            key: 'organisationName',
            render: (text: string) => <Text strong>{text}</Text>,
        },
        {
            title: 'Tokens Used',
            dataIndex: 'totalTokens',
            key: 'totalTokens',
            render: (tokens: number) => (
                <Space>
                    <ThunderboltOutlined style={{ color: '#faad14' }} />
                    <span>{formatTokenCount(tokens)}</span>
                </Space>
            ),
        },
        {
            title: 'Requests',
            dataIndex: 'totalRequests',
            key: 'totalRequests',
        },
        {
            title: 'Status',
            key: 'status',
            render: () => (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                    Active
                </Tag>
            ),
        },
    ];

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <RobotOutlined style={{ fontSize: '20px', color: '#F59E0B' }} />
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>AI Token Usage Analytics</span>
                    <Tag color="blue">Last 30 Days</Tag>
                </div>
            }
            style={{ marginBottom: 24 }}
        >
            {/* Quota Enforcement Notice */}
            {data.enforcementEnabled && (
                <Alert
                    message="AI Quota Enforcement Active"
                    description="Organizations are subject to tier-based token quotas. Quotas can be customized per organization in Organization Details. Configure global settings in System Settings → AI Cost Control."
                    type="info"
                    showIcon
                    icon={<ThunderboltOutlined />}
                    style={{ marginBottom: 24 }}
                    closable
                />
            )}

            {/* Token Usage Metrics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae7ff' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Tokens Today</Text>}
                            value={formatTokenCount(data.totalTokensToday)}
                            prefix={<FireOutlined style={{ color: '#fa8c16' }} />}
                            valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                        />
                        {data.currentUsage && (
                            <Progress 
                                percent={Math.min((data.currentUsage.currentDaily / 200000) * 100, 100)} 
                                strokeColor="#fa8c16"
                                showInfo={false}
                                size="small"
                                style={{ marginTop: 8 }}
                            />
                        )}
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Tokens This Month</Text>}
                            value={formatTokenCount(data.totalTokensThisMonth)}
                            prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                        />
                        {data.currentUsage && (
                            <Progress 
                                percent={Math.min((data.currentUsage.currentMonthly / 5000000) * 100, 100)} 
                                strokeColor="#52c41a"
                                showInfo={false}
                                size="small"
                                style={{ marginTop: 8 }}
                            />
                        )}
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Est. Daily Cost</Text>}
                            value={data.estimatedCost?.daily || 0}
                            prefix={<DollarOutlined />}
                            precision={2}
                            valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                            suffix={data.estimatedCost?.currency || 'USD'}
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Est. Monthly Cost</Text>}
                            value={data.estimatedCost?.monthly || 0}
                            prefix={<DollarOutlined />}
                            precision={2}
                            valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                            suffix={data.estimatedCost?.currency || 'USD'}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                {/* Feature Breakdown */}
                <Col xs={24} md={12}>
                    <div style={{ marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0 }}>Usage by Feature</Title>
                    </div>
                    {data.featureBreakdown?.map((feature) => (
                        <div key={feature.featureType} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <Space>
                                    <Tag color={getFeatureColor(feature.featureType)}>
                                        {formatFeatureType(feature.featureType)}
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {formatTokenCount(feature.tokens)} tokens
                                    </Text>
                                </Space>
                                <Text strong>{feature.percentage}%</Text>
                            </div>
                            <Progress 
                                percent={feature.percentage} 
                                strokeColor={getFeatureColor(feature.featureType)}
                                showInfo={false}
                                size="small"
                            />
                        </div>
                    ))}
                </Col>

                {/* Top Organizations */}
                <Col xs={24} md={12}>
                    <div style={{ marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0 }}>Top Organizations</Title>
                    </div>
                    <Table
                        dataSource={data.topOrganizations}
                        columns={topOrgColumns}
                        pagination={false}
                        size="small"
                        rowKey={(row) =>
                            row.organisationId !== null && row.organisationId !== undefined
                                ? `org-${row.organisationId}`
                                : `source-${row.source ?? 'unattributed'}`
                        }
                    />
                </Col>
            </Row>
        </Card>
    );
};